import { isMobileLayout } from '@superbet-group/web.lib.vue-utilities';
import {
    AddChildRoutes,
    AddRoutes,
    NavigationContext,
    SuperbetRouteConfig,
} from '@superbet-group/web.types';
import { isEqual as _isEqual } from 'lodash';
import Vue from 'vue';
import Router, {
    RouteRecord,
    RouterOptions,
} from 'vue-router';

import { LegacyProduct } from '@app/router/products/Product';
import { Link } from '@app/router/constants/Link';
import { RouteName } from '@app/router/constants/RouteName';
import { Title } from '@app/router/constants/Title';
import { allShellRoutes } from '@app/router/routes';
import {
    addLoginRequirements,
    checkChatHash,
    defaultLayout,
    resolveNavigationContext,
    shouldOpenLoginModal,
} from '@app/router/utils';
import {
    updateRobotsMetaTag,
    updateSchema,
} from '@app/utils/metaTags';
import InfoPage from '@pages/InfoPage.vue';
import NativeWikiPage from '@pages/NativeWikiPage.vue';
import {
    catchAllRoute,
    notFoundRoute,
} from '@pages/NotFound';
import {
    brMigrationCompleteRoute,
    idCardVerificationCaptiveRoute,
} from '@pages/IdCardVerificationCaptive';
import PageNotAuthorized from '@pages/PageNotAuthorized.vue';
import { INavigationState } from '@store/modules/navigation';

import {
    addRoutes,
    redirectRoutesOnNapoleonHub,
    getEnabledRoutes,
} from './routeHelpers';
import {
    areRoutesRegistered,
    areRoutesRegisteredPromise,
    shouldWaitForRouteRegistration,
} from './routeRegistration';
import store from '../store';
import { seoRouterOnAfterEach } from '../utils/seoEngine';
import { getConfig } from '@superbet-group/web.lib.config';
import { depthFirstSearch } from '../utils/array';
import {
    marketingAttributionTags,
    marketingSourceTags,
    sendRouteAnalytics,
} from '@app/router/analytics';
import { setPrerenderReadyState } from '../utils/prerender';
import { persistHrefLangTags } from '../utils/seoFeatures/hrefLang';
import { getAccountConfig } from '@accountFederation/config/accountConfig';
import { SiteLanguageConfig } from '@modules/app/siteLanguage/siteLanguageConfig';
import { phoneVerificationCaptiveRoute } from '@pages/PhoneVerificationCaptive';
import { authenticationService } from '@core/services/authentication/AuthenticationService';
import { clientStorageWithExpiry } from '@core/clientStorage/clientStorageWithExpiry';
import { getShellConfig } from '@src/config/shell/config';
import { recordLastVisitedTheme } from '../shared/theme/theme';
import { updateSportsCanonicalTag } from '../utils/seoFeatures/canonicalTags';

Vue.use(Router);

export const addFederationRoutes: AddRoutes = (
    routes: SuperbetRouteConfig[],
    layout: RouteRecord['components'] = defaultLayout,
    navigationContext?: NavigationContext,
) => {
    const filteredRoutes = getEnabledRoutes(LegacyProduct, routes);
    const federationRoutes = filteredRoutes.map((federationRouteConfig: SuperbetRouteConfig) => ({
        ...federationRouteConfig,
        components: {
            ...layout,
            // Overriding the default layout
            ...federationRouteConfig.components,
        },
    }));

    if (navigationContext) {
        const contextRoutes = federationRoutes.flatMap((route) => depthFirstSearch(route, 'name', 'children'));
        contextRoutes.forEach((route) => {
            // eslint-disable-next-line no-param-reassign
            route.meta = {
                navigationContext,
                ...route.meta,
            };
        });
    }

    // since federation routes can have beforeEnter hooks that redirect to each other
    // we want to add all federation routes to the router at once
    // to prevent issues when redirects worked depending on route registration order
    // note that this method is deprecated in v4 of Vue Router and solution will need to be changed then
    router.addRoutes(federationRoutes);
};

export const addFederationChildRoutes: AddChildRoutes = (
    routes: SuperbetRouteConfig[],
    parentRouteName: string,
) => {
    // NOTE: May cause race conditions if routes registered in this way depend on each other. See above.
    for (const route of routes) {
        router.addRoute(parentRouteName, route);
    }
};

export const addAuthenticatedFederationChildRoutes: AddChildRoutes = (
    routes: SuperbetRouteConfig[],
    parentRouteName: string,
) => {
    return addFederationChildRoutes(
        routes.map((route) => addLoginRequirements(route)) as SuperbetRouteConfig[],
        parentRouteName,
    );
};

const defaultRoutes: SuperbetRouteConfig[] = [
    {
        path: Link.App.Info,
        name: RouteName.App.Info,
        components: {
            ...defaultLayout,
            content: InfoPage,
        },
        meta: {
            title: Title.App.Info,
        },
    },
    {
        path: Link.Native.Wiki,
        name: RouteName.Native.Wiki,
        components: {
            content: NativeWikiPage,
        },
        meta: {
            title: '',
            layoutClass: 'native-wiki',
        },
    },
    {
        path: Link.App.NotAuthorized,
        name: RouteName.App.NotAuthorized,
        components: {
            ...defaultLayout,
            content: PageNotAuthorized,
        },
        meta: {
            title: Title.App.NotAuthorized,
        },
    },
    {
        path: Link.Native.AppsFooter,
        name: RouteName.Native.AppsFooter,
        components: {
            content: defaultLayout.footer,
        },
        meta: {
            layoutClass: 'native-footer-iframe',
            isNativeAppsFooter: true,
        },
    },
    ...(getAccountConfig().hasKycCaptive ? [idCardVerificationCaptiveRoute] : []),
    ...(getAccountConfig().hasVerifyPhoneCaptive ? [phoneVerificationCaptiveRoute] : []),
    ...(getAccountConfig().hasGenderAndNationalityRequirement ? [brMigrationCompleteRoute] : []),
    notFoundRoute,
    catchAllRoute,
];

const routerOptions: RouterOptions = {
    base: SiteLanguageConfig.hasLocaleInUrls ? getConfig().appLocale.locale.toLowerCase() : '',
    mode: 'history',
    scrollBehavior(to, from, savedPosition) {
        const toRoute = to as SuperbetRouteConfig;
        if (savedPosition && toRoute.meta?.scrollBehavior?.type === 'keepScrollPosition') {
            const scrollBehavior = toRoute.meta.scrollBehavior.options || {};
            if (scrollBehavior.behavior === 'immediate') {
                return savedPosition;
            } else if (scrollBehavior.behavior === 'awaited') {
                return new Promise((resolve) => {
                    const timeout = setTimeout(() => {
                        resolve(false as any);
                    }, scrollBehavior.maxWaitTimeMs || 1000);
                    (this as Router).app.$root.$once('triggerScroll', () => {
                        clearTimeout(timeout);
                        resolve(savedPosition);
                    });
                });
            }
            return undefined;
        }
        if (from.name === to.name && _isEqual(from.params, to.params)) {
            return undefined;
        }
        return { x: 0, y: 0 };
    },
    routes: defaultRoutes.map(redirectRoutesOnNapoleonHub),
};

const router = new Router(routerOptions);

/**
 * We need this because we have in-app back button which shouldn't navigate outside the application.
 * hasLandedOnThisRoute will help us decide when we can't longer go back.
 *
 * If we come to Superbet from YouTube and land on a route which has back:
 *  - in-app back should navigate to Superbet home
 *  - browser back should navigate to YouTube
 *
 * unused – (from the official doc) This parameter exists for historical reasons, and cannot be omitted; passing the empty string is traditional, and safe against future changes to the method.
 */
router.onReady(() => {
    history.replaceState({ ...history.state, hasLandedOnThisRoute: true }, '');
});

const filteredRoutes = getEnabledRoutes(LegacyProduct, allShellRoutes);
addRoutes(router, filteredRoutes);

router.beforeResolve(async (to, from, next) => {
    if (shouldWaitForRouteRegistration(to.name) && !areRoutesRegistered()) {
        await areRoutesRegisteredPromise;
        const currentRoute = store.getters['navigation/route'] as INavigationState['route'];
        // since loading state shows a header and user can navigate away while routes are being registered
        // prevent further execution (navigation to initial route) if the user navigated away
        // i.e. user loads '/casino' and while routes are fetched decides to navigate away to landing page from the header
        // then we do not want to navigate them back to '/casino' once route registration finishes
        //
        // we might programatically redirect to certain routes such as NotFound route
        // while routes registration is still in progress
        // i.e. user loads an article that does not exist
        // in that case we should not prevent that navigation
        const excludedRouteNames: string[] = [
            RouteName.App.NotFound,
            RouteName.App.NotAuthorized,
        ];

        if (
            currentRoute?.name != null
            && currentRoute.fullPath !== to.fullPath
            && to.name != null
            && !excludedRouteNames.includes(to.name)
        ) {
            return;
        }

        next({ path: to.fullPath });
    } else {
        next();
    }
});

// This callback runs before every route change, including on page load.
// eslint-disable-next-line sonarjs/cognitive-complexity
router.beforeEach((to, from, next) => {
    // These parse marketing attribution data from query parameters and saves
    // them to local-storage. These are needed in order to attribute actions
    // such as registration to specific campaigns. It allows us to measure the
    // efficiency of our marketing spend - and to optimise it.
    // * Removing this or refactoring this may break our ability to measure return
    //   of investment.
    // * Note that this must be run *before* we change the current route or perform
    //   a redirect. If not we will lose the query parameters.
    marketingSourceTags(to);
    if (!from.name) {
        marketingAttributionTags(to);
    }

    // if routes are not registered yet, it would pick up as if we were on a 404
    // we do not want that so just continue and return early
    if (shouldWaitForRouteRegistration(to.name) && !areRoutesRegistered()) {
        next();
    } else {
        store.dispatch('ui/help/setHelpKey', { path: to.path });
        if (isMobileLayout() && to.name === RouteName.App.Home && from.name === RouteName.Sport.PrematchOffer
            || to.name === RouteName.Sport.PrematchOffer && from.name === RouteName.App.Home) {
            // do nothing because they're basically the same page
        }
        updateRobotsMetaTag(to, router.options.base);
        checkChatHash(to, next);
        shouldOpenLoginModal(to);
        updateSchema(to);
        next();
    }
});

router.afterEach((to, from) => {
    // Note that we are willfully ignoring the promises here
    // in order to avoid blocking - and they will finish
    // asynchronously.
    persistHrefLangTags(to);

    sendRouteAnalytics(to, from);

    // clear any previous canonical tags
    document.querySelector('link[rel=canonical]')?.remove();

    updateSportsCanonicalTag(to, router);

    seoRouterOnAfterEach(to, from, store);

    resolveNavigationContext(to, store);

    setPrerenderReadyState(true);

    recordLastVisitedTheme();

    if (getShellConfig().remoteConfig.checkLoginSessionOnNavigation && clientStorageWithExpiry.get('user') !== null) {
        authenticationService.throttledCheckLoginSession(false);
    }
});

export default router;
